import { useQuery } from '@tanstack/react-query'
import { api } from './api'

/**
 * Resolve the CURRENT viewer's effective access level for a Space, used
 * to gate write controls (comment box, Files & Links Upload / Add Link)
 * client-side so read-only members never even see actions the backend
 * would reject.
 *
 * Reads the same `['space', spaceId]` query SpacePage already populates,
 * so in the Space view this is a pure cache hit (no extra request). On
 * other surfaces (Task Monitor, Team Tasks) it fetches once per distinct
 * spaceId and React Query dedupes across every row sharing that Space.
 *
 * The server attaches `myAccessLevel` to the single-space GET
 * (spaces.service.findById). When it's absent (older API, or spaceId not
 * supplied) we FAIL OPEN — returning permissive flags — because the
 * backend still enforces every rule; the client gate is a UX nicety, not
 * the security boundary.
 *
 * Returns:
 *   - accessLevel : 'view' | 'comment' | 'edit' | 'full' | null
 *   - canComment  : may post/edit/delete comments + react (comment+)
 *   - canModify   : may upload files / add links / edit task data (edit+)
 *   - isLoading   : the access query is still resolving
 */
export function useSpaceAccess(spaceId) {
  const query = useQuery({
    queryKey: ['space', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}`)
      return res.data
    },
    enabled: Boolean(spaceId),
    staleTime: 60_000,
    retry: false,
  })

  const accessLevel = query.data?.myAccessLevel ?? null

  // Fail-open when we don't know the level yet (loading) or the API
  // didn't supply it — the backend remains the enforcing authority.
  const known = accessLevel === 'view' || accessLevel === 'comment' ||
    accessLevel === 'edit' || accessLevel === 'full'

  const canComment = !known || accessLevel !== 'view'
  const canModify = !known || accessLevel === 'edit' || accessLevel === 'full'

  return {
    accessLevel,
    canComment,
    canModify,
    isLoading: query.isLoading,
  }
}
